(function () {
  const TRANSITION_DURATION = 1000;

  const initCollectionsBar = (section) => {
    const bar = section.querySelector(".js-showcase-collections");
    if (!bar) return;

    const ro = new ResizeObserver((entries) => {
      const h = entries[0].contentRect.height;
      section.style.setProperty("--collections-half-height", `${h / 2}px`);
    });
    ro.observe(bar);

    let ticking = false;

    bar.style.setProperty("--expand-progress", 0);

    const update = () => {
      if (section.getBoundingClientRect().top >= 0) {
        bar.style.setProperty("--expand-progress", 0);
        ticking = false;
        return;
      }

      const rect = bar.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.max(0, Math.min(1, (vh - rect.top) / vh));
      bar.style.setProperty("--expand-progress", progress);
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    bar._destroyBar = () => {
      ro.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  };

  const initControlsClearance = (section, sliderEl) => {
    const controls = section.querySelector(".showcase-slideshow__controls");
    if (!controls || !sliderEl) return;

    const update = () => {
      sliderEl.style.setProperty(
        "--ss-controls-height",
        `${controls.offsetHeight}px`
      );
    };

    const ro = new ResizeObserver(update);
    ro.observe(controls);
    update();

    controls._destroyClearance = () => {
      ro.disconnect();
      sliderEl.style.removeProperty("--ss-controls-height");
    };
  };

  const initAutoplayObserver = (section, sliderEl, swiper) => {
    const hasAutoplay = sliderEl.getAttribute("data-autoplay") === "true";

    let wasIntersecting = null;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!swiper?.enabled) return;
        if (wasIntersecting === entry.isIntersecting) return;
        wasIntersecting = entry.isIntersecting;

        if (entry.isIntersecting) {
          if (hasAutoplay && swiper.autoplay) swiper.autoplay.resume();
          playVideo(swiper);
        } else {
          if (hasAutoplay && swiper.autoplay) swiper.autoplay.pause();
          stopVideo(swiper);
        }
      });
    });

    observer.observe(section);

    sliderEl._destroyAutoplayObserver = () => {
      observer.disconnect();
    };
  };

  // ── Video / embed playback ──────────────────────────────────────────────────

  const controlEmbed = (iframe, action) => {
    if (!iframe) return;
    if (iframe.classList.contains("js-youtube")) {
      const func = action === "play" ? "playVideo" : "pauseVideo";
      iframe.contentWindow.postMessage(
        `{"event":"command","func":"${func}","args":""}`,
        "*"
      );
    } else if (iframe.classList.contains("js-vimeo")) {
      iframe.contentWindow.postMessage(`{"method":"${action}"}`, "*");
    }
  };

  const playSlideMedia = (slide) => {
    if (!slide) return;
    const video = slide.querySelector("video.showcase-slide__video");
    if (video) video.play().catch(() => {});
    controlEmbed(slide.querySelector("iframe.showcase-slide__video"), "play");
  };

  const pauseSlideMedia = (slide) => {
    if (!slide) return;
    const video = slide.querySelector("video.showcase-slide__video");
    if (video) video.pause();
    controlEmbed(slide.querySelector("iframe.showcase-slide__video"), "pause");
  };

  const playVideo = (swiper) => {
    if (!swiper?.enabled) return;
    const prevSlide = swiper.slides[swiper.previousIndex];
    const activeSlide = swiper.slides[swiper.activeIndex];

    playSlideMedia(activeSlide);
    if (prevSlide && prevSlide !== activeSlide) pauseSlideMedia(prevSlide);
  };

  const stopVideo = (swiper) => {
    if (!swiper?.enabled) return;
    pauseSlideMedia(swiper.slides[swiper.activeIndex]);
  };

  const initSingleSlideVideo = (section) => {
    const slide = section.querySelector(
      ".showcase-slideshow__swiper:not(.swiper) .showcase-slide"
    );
    if (!slide || !slide.querySelector(".showcase-slide__video")) return;

    if (slide._destroySingleVideoObserver) slide._destroySingleVideoObserver();

    let wasIntersecting = null;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (wasIntersecting === entry.isIntersecting) return;
        wasIntersecting = entry.isIntersecting;
        entry.isIntersecting ? playSlideMedia(slide) : pauseSlideMedia(slide);
      });
    });

    observer.observe(section);

    slide._destroySingleVideoObserver = () => observer.disconnect();
  };

  // ── Word splitting ──────────────────────────────────────────────────────────

  const wrapWords = (node, ref) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (!/\S/.test(node.textContent)) return;
      const parts = node.textContent.split(/(\s+)/);
      const frag = document.createDocumentFragment();
      parts.forEach((part) => {
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
        } else if (part) {
          const span = document.createElement("span");
          span.className = "ss-word";
          span.style.setProperty("--wi", ref.i++);
          span.textContent = part;
          frag.appendChild(span);
        }
      });
      node.parentNode.replaceChild(frag, node);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (
      ["BUTTON", "INPUT", "SELECT", "TEXTAREA", "SCRIPT", "STYLE"].includes(
        node.tagName
      )
    )
      return;
    Array.from(node.childNodes).forEach((child) => wrapWords(child, ref));
  };

  const HEADING_SELECTOR = ".block-heading, .block-subheading";

  const splitWords = (slide) => {
    const content = slide.querySelector(".showcase-slide__content");
    if (!content || content.dataset.wordsSplit) return;
    content.dataset.wordsSplit = "1";

    const ref = { i: 0 };

    // Every block dropped into the slide gets an entrance animation:
    // heading/subheading word-by-word, everything else as a single unit.
    Array.from(content.children).forEach((el) => {
      if (el.matches(HEADING_SELECTOR)) {
        wrapWords(el, ref);
      } else {
        el.classList.add("ss-block");
        el.style.setProperty("--wi", ref.i++);
      }
    });
  };

  // ── Section init ────────────────────────────────────────────────────────────

  const prepareSlideContent = (slide) => {
    if (!slide) return;
    splitWords(slide);
    slide.classList.add("ss-prepared");
  };

  const restartContentAnimation = (slide) => {
    if (!slide) return;
    slide.classList.add("ss-reset");
    slide.classList.remove("ss-animate");
    void slide.offsetWidth;
    slide.classList.remove("ss-reset");
    void slide.offsetWidth;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        slide.classList.add("ss-animate");
      });
    });
  };

  const playContentEntrance = (slide) => {
    if (!slide) return;
    prepareSlideContent(slide);
    slide.classList.add("is-active");
    restartContentAnimation(slide);
  };

  const initShowcase = (section) => {
    if (!section?.classList.contains("showcase-slideshow-section")) return;

    const sliderEl = section.querySelector(".js-showcase-swiper");
    if (!sliderEl) {
      initSingleSlideVideo(section);
      initCollectionsBar(section);
      playContentEntrance(section.querySelector(".showcase-slide"));
      return;
    }

    if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);

    if (sliderEl._destroyAutoplayObserver) {
      sliderEl._destroyAutoplayObserver();
    }

    const existingBar = section.querySelector(".js-showcase-collections");
    if (existingBar?._destroyBar) {
      existingBar._destroyBar();
    }

    const existingControls = section.querySelector(
      ".showcase-slideshow__controls"
    );
    if (existingControls?._destroyClearance) {
      existingControls._destroyClearance();
    }

    const paginationEl = section.querySelector(
      ".showcase-slideshow__pagination"
    );
    const nextBtn = section.querySelector(".slider-nav-button--next");
    const prevBtn = section.querySelector(".slider-nav-button--prev");

    const sliderLoop = sliderEl.getAttribute("data-loop") === "true";
    const hasAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
    const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 5000);

    const slidesBeforeInit = sliderEl.querySelectorAll(".showcase-slide");
    slidesBeforeInit.forEach((slide) => {
      slide.classList.add("ss-reset");
      slide.classList.remove("ss-animate");
      prepareSlideContent(slide);
    });

    let cleanupTimer = null;

    const cleanupSlides = (slides) => {
      slides.forEach((slide) => {
        slide.classList.remove(
          "is-entering-next",
          "is-entering-prev",
          "is-leaving"
        );
      });
    };

    const scheduleCleanup = (s) => {
      clearTimeout(cleanupTimer);
      cleanupTimer = setTimeout(() => {
        cleanupSlides(s.slides);
        s.animating = false;
        s.wrapperEl?.dispatchEvent(new Event("transitionend"));
      }, TRANSITION_DURATION + 50);
    };

    const onTransitionStart = (s, direction) => {
      const incoming = s.slides[s.activeIndex];
      const leaving = s.slides[s.previousIndex];

      cleanupSlides(s.slides);

      if (leaving && leaving !== incoming) {
        leaving.classList.add("is-leaving");
      }

      if (incoming) {
        void incoming.offsetWidth;
        incoming.classList.add(
          direction === "next" ? "is-entering-next" : "is-entering-prev"
        );
      }

      setTimeout(() => {
        s.animating = true;
      });

      scheduleCleanup(s);
    };

    const swiper = new Swiper(sliderEl, {
      loop: sliderLoop,
      slidesPerView: 1,
      speed: 0,
      virtualTranslate: true,
      resistanceRatio: 0,
      allowTouchMove: true,
      grabCursor: false,
      mousewheel: {
        forceToAxis: true,
      },
      autoplay: hasAutoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
          }
        : false,
      pagination: {
        el: paginationEl,
        clickable: true,
        renderBullet(_index, className) {
          return `<span class="${className}"><svg class="bullet-spinner" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><circle class="bullet-spinner__track" cx="7" cy="7" r="5"/><circle class="bullet-spinner__arc" cx="7" cy="7" r="5" transform="rotate(-90 7 7)"/></svg></span>`;
        },
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
        disabledClass: "swiper-button-disabled",
      },
      on: {
        init(s) {
          const active = s.slides[s.activeIndex];
          s.slides.forEach((slide) => {
            if (slide !== active) {
              slide.classList.add("ss-reset");
              slide.classList.remove("is-active", "ss-animate");
            }
          });
          if (active) {
            active.classList.add("is-active");
            restartContentAnimation(active);
          }
        },

        autoplayTimeLeft(_s, _time, progress) {
          const arc = paginationEl?.querySelector(
            ".swiper-pagination-bullet-active .bullet-spinner__arc"
          );
          if (arc) arc.style.strokeDashoffset = 31.416 * progress;
        },

        slideChange(s) {
          const active = s.slides[s.activeIndex];
          s.slides.forEach((slide) => {
            if (slide !== active) {
              slide.classList.add("ss-reset");
              slide.classList.remove("is-active", "ss-animate");
            }
          });
          if (active) {
            active.classList.add("is-active");
            restartContentAnimation(active);
          }

          paginationEl
            ?.querySelectorAll(".bullet-spinner__arc")
            .forEach((arc) => {
              arc.style.strokeDashoffset = "";
            });

          playVideo(s);
        },

        slideNextTransitionStart(s) {
          onTransitionStart(s, "next");
        },

        slidePrevTransitionStart(s) {
          onTransitionStart(s, "prev");
        },
      },
    });

    initAutoplayObserver(section, sliderEl, swiper);
    initCollectionsBar(section);
    initControlsClearance(section, sliderEl);
  };

  initShowcase(document.currentScript.parentElement);

  if (window.__showcaseSlideshowListenersBound) return;
  window.__showcaseSlideshowListenersBound = true;

  document.addEventListener("shopify:section:load", (e) => {
    initShowcase(e.target);
  });

  document.addEventListener("shopify:section:unload", (e) => {
    const sliderEl = e.target.querySelector(".js-showcase-swiper");
    if (sliderEl?._destroyAutoplayObserver) {
      sliderEl._destroyAutoplayObserver();
    }
    if (sliderEl?.swiper) sliderEl.swiper.destroy(true, true);

    const bar = e.target.querySelector(".js-showcase-collections");
    if (bar?._destroyBar) bar._destroyBar();

    const controls = e.target.querySelector(".showcase-slideshow__controls");
    if (controls?._destroyClearance) controls._destroyClearance();

    const singleSlide = e.target.querySelector(
      ".showcase-slideshow__swiper:not(.swiper) .showcase-slide"
    );
    if (singleSlide?._destroySingleVideoObserver) {
      singleSlide._destroySingleVideoObserver();
    }
  });
})();
